// src/pages/Accounts.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/sections/Navbar";
import Breadcrumb from "../components/ui/Breadcrumb";
import PageHeader from "../components/sections/PageHeader";
import Tabs from "../components/sections/Tabs";
import GebruikersTable from "./tabs/GebruikersTab";
import OrganisatieTable from "./tabs/OrganisatiesTab";
import ProfileSection from "../components/sections/ProfileSection";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { useMessages } from "../context/MessageContext";
import "./AccountsModal.css";

const emptyProfile = {
  first_name: "",
  email: "",
  role: "user",
  region: null,
  gemeente: null,
  function_title: "",
  function_description: "",
  password: "",
};

function Accounts() {
  const { tokens, user: authUser, isVerifying, authorizedFetch } = useAuth();
  const { addMessage } = useMessages();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalState, setModalState] = useState({ mode: null, user: null });
  const [formState, setFormState] = useState(emptyProfile);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locationOptions, setLocationOptions] = useState({ regions: [], gemeentes: [] });

  const isAdminOrBetter = authUser?.role === "admin" || authUser?.role === "superadmin";

  const roleOptions = useMemo(() => {
    if (authUser?.role === "superadmin") {
      return [
        { value: "superadmin", label: "Superadmin" },
        { value: "admin", label: "Admin" },
        { value: "user", label: "User" },
      ];
    }
    return [
      { value: "admin", label: "Admin" },
      { value: "user", label: "User" },
    ];
  }, [authUser?.role]);

  const fetchUsers = useCallback(async () => {
    if (!tokens?.access) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authorizedFetch("/api/users/", {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const payload = await response.json().catch(() => []);

      if (!response.ok) {
        throw new Error(payload?.detail || "Kon gebruikers niet laden.");
      }

      setUsers(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setUsers([]);
      setError(err.message || "Kon gebruikers niet laden.");
    } finally {
      setLoading(false);
    }
  }, [tokens?.access, authorizedFetch]);

  useEffect(() => {
    if (isVerifying) {
      return;
    }
    fetchUsers();
    if (tokens?.access) {
      authorizedFetch("/api/account_locations/")
        .then((res) => res.json())
        .then((data) => setLocationOptions(data || { regions: [], gemeentes: [] }))
        .catch(() => setLocationOptions({ regions: [], gemeentes: [] }));
    } else {
      setLocationOptions({ regions: [], gemeentes: [] });
    }
  }, [fetchUsers, isVerifying, tokens?.access, authorizedFetch]);

  const normalizedUsers = useMemo(() => {
    return users
      .filter((user) => user.role !== "superadmin")
      .map((user) => ({
        ...user,
        name:
          [user.first_name, user.last_name].filter(Boolean).join(" ") ||
          user.username ||
          user.email ||
          "Onbekende gebruiker",
      }));
  }, [users]);

  const administrators = useMemo(
    () => normalizedUsers.filter((user) => user.role === "admin"),
    [normalizedUsers]
  );
  const regularUsers = useMemo(
    () => normalizedUsers.filter((user) => user.role === "user"),
    [normalizedUsers]
  );

  const handleProfileChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openCreateModal = useCallback((initialValues = {}) => {
    setFormState({
      ...emptyProfile,
      ...initialValues,
    });
    setModalError(null);
    setModalState({ mode: "create", user: null });
  }, []);

  const openEditModal = (user) => {
    setFormState({
      first_name: user.first_name || "",
      email: user.email || "",
      role: user.role || "user",
      region: user.region || null,
      gemeente: user.gemeente || null,
      function_title: user.function_title || "",
      function_description: user.function_description || "",
      password: "",
    });
    setModalError(null);
    setModalState({ mode: "edit", user });
  };

  const closeModal = () => {
    setModalState({ mode: null, user: null });
    setFormState(emptyProfile);
    setModalError(null);
    setSaving(false);
  };

  const handleModalSave = async () => {
    if (!modalState.mode || !tokens?.access) {
      return;
    }

    if (modalState.mode === "edit" && !modalState.user) {
      setModalError("Geen gebruiker geselecteerd.");
      return;
    }

    const trimmedFirstName = (formState.first_name || "").trim();
    const trimmedEmail = (formState.email || "").trim().toLowerCase();
    const trimmedFunctionTitle = (formState.function_title || "").trim();
    const trimmedDescription = (formState.function_description || "").trim();
    const trimmedPassword = (formState.password || "").trim();

    if (!trimmedFirstName) {
      setModalError("Voornaam is verplicht.");
      return;
    }
    if (!trimmedEmail) {
      setModalError("E-mailadres is verplicht.");
      return;
    }
    if (modalState.mode === "create" && !trimmedPassword) {
      setModalError("Wachtwoord is verplicht.");
      return;
    }

    const payload = {
      first_name: trimmedFirstName,
      email: trimmedEmail,
      role: formState.role || "user",
      region: formState.region || null,
      gemeente: formState.gemeente || null,
      function_title: trimmedFunctionTitle,
      function_description: trimmedDescription,
    };

    if (modalState.mode === "create") {
      payload.username = trimmedEmail;
      payload.password = trimmedPassword;
    }
    if (modalState.mode === "edit" && trimmedPassword) {
      payload.password = trimmedPassword;
    }

    const url =
      modalState.mode === "create"
        ? "/api/users/"
        : `/api/users/${modalState.user.id}/`;
    const method = modalState.mode === "create" ? "POST" : "PATCH";

    setSaving(true);
    setModalError(null);

    try {
      const response = await authorizedFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            (modalState.mode === "create"
              ? "Kon gebruiker niet aanmaken."
              : "Kon gebruiker niet bijwerken.")
        );
      }

      addMessage(
        "success",
        modalState.mode === "create"
          ? "Gebruiker is toegevoegd."
          : "Gebruiker is bijgewerkt."
      );
      await fetchUsers();
      closeModal();
    } catch (err) {
      setModalError(err.message || "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const quickAddEmail = location.state?.quickAddEmail;

  useEffect(() => {
    if (!isVerifying && isAdminOrBetter && quickAddEmail) {
      openCreateModal({ email: quickAddEmail });
      navigate(location.pathname, { replace: true });
    }
  }, [isVerifying, isAdminOrBetter, quickAddEmail, openCreateModal, navigate, location.pathname]);

  return (
    <div>
      <Navbar />
      <div className="container">
        <Breadcrumb icon="home-line.png" items={[]} current="Accounts" />
        <PageHeader title="Accounts" />
        {error && <p className="accounts-error">{error}</p>}

        <Tabs
          tabs={[
            {
              key: "administrators",
              label: "Administrators",
              render: () => (
                <GebruikersTable
                  title="Administrators"
                  description="Overzicht van alle beheerders."
                  emptyMessage="Nog geen administrators aanwezig."
                  users={administrators}
                  isLoading={loading}
                  onAddUser={() => openCreateModal({ role: "admin" })}
                  onEditUser={openEditModal}
                  addLabel="Voeg beheerder toe"
                  countLabel={`${administrators.length} administrators`}
                />
              ),
            },
            {
              key: "gebruikers",
              label: "Gebruikers",
              render: () => (
                <GebruikersTable
                  title="Gebruikers"
                  description="Beheer hier alle gebruikers binnen ContinuMee."
                  emptyMessage="Nog geen gebruikers aanwezig."
                  users={regularUsers}
                  isLoading={loading}
                  onAddUser={openCreateModal}
                  onEditUser={openEditModal}
                  addLabel="Voeg gebruiker toe"
                  countLabel={`${regularUsers.length} gebruikers`}
                />
              ),
            },
            {
              key: "organisaties",
              label: "Organisaties",
              render: () => <OrganisatieTable />,
            },
            ...(authUser?.role === "superadmin"
              ? [
                  {
                    key: "onderzoek-management",
                    label: "Onderzoek beheer",
                    render: () => (
                      <section className="accounts-dashboard-management">
                        <div>
                          <h2>Onderzoek beheer</h2>
                          <p>
                            Bepaal welke actieve regio&apos;s toegang hebben tot elk
                            onderzoek en bekijk de bijbehorende vragenlijsten.
                          </p>
                        </div>
                        <Button onClick={() => navigate("/onderzoek-beheer")}>
                          Beheer onderzoeken
                        </Button>
                      </section>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>

      <UserProfileModal
        isOpen={Boolean(modalState.mode)}
        mode={modalState.mode}
        profile={formState}
        onChange={handleProfileChange}
        onSave={handleModalSave}
        onClose={closeModal}
        saving={saving}
        error={modalError}
        roleOptions={roleOptions}
        locationOptions={locationOptions}
      />
    </div>
  );
}

function UserProfileModal({
  isOpen,
  mode,
  profile,
  onChange,
  onSave,
  onClose,
  saving,
  error,
  roleOptions,
  locationOptions,
}) {
  if (!isOpen) {
    return null;
  }

  const isCreate = mode === "create";

  return (
    <div className="accounts-modal-overlay">
      <div className="accounts-modal-content">
        <div className="accounts-modal-header">
          <h2>{isCreate ? "Nieuwe gebruiker" : "Gebruiker bewerken"}</h2>
          <p>
            {isCreate
              ? "Vul het profiel in om een nieuwe gebruiker toe te voegen."
              : "Werk de gegevens bij en sla de wijziging op."}
          </p>
        </div>

        <ProfileSection
          profile={profile}
          onChange={onChange}
          readOnly={false}
          roleOptions={roleOptions}
          locationOptions={locationOptions}
          showPassword={isCreate}
        />

        {error && <p className="accounts-modal-error">{error}</p>}

        <div className="accounts-modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Annuleren
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>
            {saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Accounts;
